import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Timer, Star, FastForward, IndianRupee, Zap, ThumbsUp, Target, ArrowUp } from 'lucide-react';
import content from '../data/content.json';

const UseCases = () => {
  const { hero, filterLabel, cta, items: useCases } = content.useCases;
  const [selectedIndustry, setSelectedIndustry] = useState('All');

  const industries = ['All', ...new Set(useCases.map(useCase => useCase.industry))];

  const filteredUseCases = selectedIndustry === 'All' 
    ? useCases 
    : useCases.filter(useCase => useCase.industry === selectedIndustry);

  const getMetricIcon = (metric) => {
    if (metric === "25% increase") return Star;
    if (metric === "Reduced from 5 days to 1 day") return Zap;
    if (metric === "On-time delivery increased by 40%") return ThumbsUp;
    if (metric === "95% client satisfaction rate") return Star;
    if (metric === "Zero missed publication deadlines") return Target;
    if (metric === "Increased by 60%") return ArrowUp;
    
    if (metric.includes('hours') || metric.includes('time')) return Timer;
    if (metric.includes('faster') || metric.includes('speed')) return FastForward;
    if (metric.includes('cost') || metric.includes('₹') || metric.includes('%') && !metric.includes('increase') && !metric.includes('satisfaction')) return IndianRupee;
    if (metric.includes('satisfaction')) return Star;
    
    return Timer;
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="pad-2xl">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="heading-1 mb-6">
              {hero.title}
            </h1>
            <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
              {hero.subtitle}
            </p>
            <Link to="/free-audit" className="btn-orange">
              {hero.ctaText}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Industry Filter */}
      <section className="pad-lg" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
            <span className="body-medium font-medium">{filterLabel}</span>
            <div className="flex flex-wrap gap-2">
              {industries.map(industry => (
                <button
                  key={industry}
                  onClick={() => setSelectedIndustry(industry)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedIndustry === industry 
                      ? 'text-white' 
                      : 'hover:bg-gray-100'
                  }`}
                  style={{
                    background: selectedIndustry === industry ? 'var(--gradient-button)' : 'transparent',
                    border: selectedIndustry === industry ? 'none' : '1px solid var(--border-light)',
                    color: selectedIndustry === industry ? 'white' : 'var(--text-body)'
                  }}
                >
                  {industry}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="pad-2xl">
        <div className="container">
          <div className="space-y-12">
            {filteredUseCases.map((useCase, index) => (
              <div key={useCase.id} className="max-w-4xl mx-auto">
                <div className="product-card">
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Left Column - Details */}
                    <div>
                      <span 
                        className="px-3 py-1 rounded-full text-sm font-medium mb-4 inline-block"
                        style={{ 
                          background: 'var(--accent-wash)',
                          color: 'var(--accent-text)'
                        }}
                      >
                        {useCase.industry}
                      </span>
                      
                      <h2 className="heading-2 mb-4">{useCase.title}</h2>
                      
                      <div className="mb-6">
                        <h3 className="heading-3 mb-2" style={{ color: '#dc2626' }}>Challenge</h3>
                        <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                          {useCase.challenge}
                        </p>
                      </div>
                      
                      <div className="mb-6">
                        <h3 className="heading-3 mb-2" style={{ color: 'var(--accent-text)' }}>Solution</h3>
                        <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                          {useCase.solution}
                        </p>
                      </div>
                      
                      <div>
                        <h3 className="heading-3 mb-3">Automated Processes</h3>
                        <ul className="space-y-2">
                          {useCase.automatedProcesses.map((process, idx) => (
                            <li key={idx} className="flex items-center body-medium">
                              <div className="w-2 h-2 rounded-full mr-3" style={{ background: 'var(--accent-primary)' }}></div>
                              {process}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    
                    {/* Right Column - Results */}
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <h3 className="heading-3 mb-6 text-center">Results Achieved</h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {Object.entries(useCase.results).map(([key, value], idx) => {
                          const IconComponent = getMetricIcon(value);
                          const metricName = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                          
                          return (
                            <div key={idx} className="text-center p-4 bg-white rounded-lg">
                              <IconComponent 
                                className="h-8 w-8 mx-auto mb-2" 
                                style={{ color: 'var(--accent-text)' }} 
                              />
                              <div className="heading-3 mb-1" style={{ color: 'var(--accent-text)' }}>
                                {value}
                              </div>
                              <div className="caption">
                                {metricName}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pad-2xl">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-2 mb-4">{cta.title}</h2>
            <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
              {cta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/free-audit" className="btn-orange">
                {cta.primaryCta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link to="/services" className="btn-secondary">
                {cta.secondaryCta}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default UseCases;
